import { useQuery } from '@tanstack/react-query';
import {
  Users as UsersIcon,
  Contact,
  CalendarDays,
  Tags,
  Package,
  ShoppingBag,
  GraduationCap,
  Receipt,
} from 'lucide-react';
import { adminApi, type OrgUsage, type OrgUsageBreakdown } from '@/api/admin';
import { Loading } from '@/components/ui';

type CardKey = keyof OrgUsage;

const CARDS: { key: CardKey; label: string; icon: typeof UsersIcon }[] = [
  { key: 'users', label: 'Users', icon: UsersIcon },
  { key: 'contacts', label: 'Contacts', icon: Contact },
  { key: 'events', label: 'Events', icon: CalendarDays },
  { key: 'event_categories', label: 'Event categories', icon: Tags },
  { key: 'services', label: 'Services', icon: Package },
  { key: 'crm_products', label: 'CRM products', icon: ShoppingBag },
  { key: 'courses', label: 'Courses', icon: GraduationCap },
  { key: 'purchased_products', label: 'Purchased products', icon: Receipt },
];

function StatusChips({ breakdown }: { breakdown: OrgUsageBreakdown }) {
  const entries = Object.entries(breakdown.by_status ?? {});
  if (!entries.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1.5">
      {entries
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .map(([status, count]) => (
          <span
            key={status}
            className="rounded-md border border-navy-border bg-navy/40 px-2 py-0.5 text-xs text-ink-secondary"
          >
            <span className="text-ink-primary">{status}</span>
            <span className="mx-1 text-ink-muted">·</span>
            <span className="num">{count}</span>
          </span>
        ))}
    </div>
  );
}

export default function OrgUsageTab({ orgId }: { orgId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-org-usage', orgId],
    queryFn: () => adminApi.getOrgUsage(orgId),
    staleTime: 60_000,
  });

  if (isLoading) return <Loading text="Loading usage..." />;
  if (error) {
    return (
      <div className="card p-6 text-sm text-danger">
        Failed to load usage: {(error as Error).message}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {CARDS.map(({ key, label, icon: Icon }) => {
        const breakdown = (data[key] ?? { total: 0 }) as OrgUsageBreakdown;
        return (
          <div key={key} className="card p-5">
            <div className="mb-3 flex items-center gap-2 text-sm text-ink-secondary">
              <Icon className="h-4 w-4 text-indigo-400" />
              {label}
            </div>
            <div className="num text-3xl font-bold text-ink-primary">
              {breakdown.total.toLocaleString()}
            </div>
            <StatusChips breakdown={breakdown} />
          </div>
        );
      })}
    </div>
  );
}
